import { useState, useEffect } from "react";
import { 
  UserPlus, Building2, Wrench, Package, CheckCircle2, 
  XCircle, Clock, Send, ChevronRight, AlertTriangle, Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function AccessRequestsWidget() {
  const [requests, setRequests] = useState({ contractor_requests: [], supplier_requests: [] });
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [activeTab, setActiveTab] = useState("contractor");
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch requests after a short delay to ensure auth is ready
    const timer = setTimeout(() => {
      fetchRequests();
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const fetchRequests = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${API_URL}/api/admin/access-requests?status=pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRequests(data);
      } else if (response.status === 401) {
        // Token invalid or expired - just don't show widget
        setError("unauthorized");
      }
    } catch (error) {
      console.error("Failed to fetch access requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvite = async (request, type) => {
    const isContractor = type === "contractor";
    setProcessingId(request.email);
    
    try {
      const token = localStorage.getItem("token");
      
      // First, create an invite for the requester
      const endpoint = isContractor 
        ? `${API_URL}/api/contractors/invite`
        : `${API_URL}/api/suppliers/invite`;
      
      const inviteData = isContractor ? {
        company_name: request.business_name,
        email: request.email,
        phone: request.phone,
        specialties: [request.trade],
        city: request.city,
        state: request.state,
        trade_type: request.trade
      } : {
        company_name: request.company_name,
        email: request.email,
        phone: request.phone,
        city: request.city,
        state: request.state,
        business_type: request.business_type
      };
      
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(inviteData)
      });
      
      if (response.ok) {
        // Update the request status
        const collection = isContractor ? "contractor_access_requests" : "supplier_access_requests";
        await fetch(`${API_URL}/api/admin/update-request-status`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            collection,
            email: request.email,
            status: "invited"
          })
        });
        
        toast.success(`Invite sent to ${request.email}!`);
        fetchRequests(); // Refresh the list
      } else {
        throw new Error("Failed to send invite");
      }
    } catch (error) {
      toast.error("Failed to send invite. Please try again.");
    } finally {
      setProcessingId(null);
    }
  };

  const handleDecline = async (request, type) => {
    const isContractor = type === "contractor";
    setProcessingId(request.email);
    
    try {
      const token = localStorage.getItem("token");
      const collection = isContractor ? "contractor_access_requests" : "supplier_access_requests";
      
      await fetch(`${API_URL}/api/admin/update-request-status`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          collection,
          email: request.email,
          status: "declined"
        })
      });
      
      toast.success("Request declined");
      fetchRequests();
    } catch (error) {
      toast.error("Failed to update request");
    } finally {
      setProcessingId(null);
    }
  };

  const totalPending = requests.contractor_requests.length + requests.supplier_requests.length;

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-slate-200 rounded w-1/3 mb-4"></div>
          <div className="h-20 bg-slate-100 rounded"></div>
        </div>
      </div>
    );
  }

  // Don't show widget if there's an error or no token
  if (error) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Network Access Requests</h3>
              <p className="text-emerald-100 text-sm">One-click invite for new contractors & suppliers</p>
            </div>
          </div>
          {totalPending > 0 && (
            <div className="bg-white/20 px-3 py-1 rounded-full">
              <span className="text-white font-bold">{totalPending}</span>
              <span className="text-emerald-100 text-sm ml-1">pending</span>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200">
        <button
          onClick={() => setActiveTab("contractor")}
          className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
            activeTab === "contractor" 
              ? "text-emerald-600 border-b-2 border-emerald-600 bg-emerald-50/50" 
              : "text-slate-500 hover:text-slate-700"
          }`}
        >
          <Wrench className="w-4 h-4" />
          Contractors
          {requests.contractor_requests.length > 0 && (
            <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-xs">
              {requests.contractor_requests.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab("supplier")}
          className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
            activeTab === "supplier" 
              ? "text-violet-600 border-b-2 border-violet-600 bg-violet-50/50" 
              : "text-slate-500 hover:text-slate-700"
          }`}
        >
          <Package className="w-4 h-4" />
          Suppliers
          {requests.supplier_requests.length > 0 && (
            <span className="bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full text-xs">
              {requests.supplier_requests.length}
            </span>
          )}
        </button>
      </div>

      {/* Content */}
      <div className="p-4 max-h-96 overflow-y-auto">
        {activeTab === "contractor" && (
          <>
            {requests.contractor_requests.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Wrench className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                <p>No pending contractor requests</p>
              </div>
            ) : (
              <div className="space-y-3">
                {requests.contractor_requests.map((request, index) => (
                  <div 
                    key={request.email || index}
                    className="bg-slate-50 rounded-lg p-4 border border-slate-200 hover:border-emerald-300 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-slate-900">{request.business_name}</h4>
                        <p className="text-sm text-slate-600">{request.contact_name || "—"}</p>
                      </div>
                      <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">
                        {request.trade?.replace("_", " ").toUpperCase() || "General"}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-sm text-slate-600 mb-3">
                      <div>📧 {request.email}</div>
                      <div>📞 {request.phone}</div>
                      <div>📍 {request.city}, {request.state}</div>
                      <div>🪪 {request.license_number || "Not provided"}</div>
                    </div>
                    
                    {request.message && (
                      <p className="text-sm text-slate-500 italic mb-3 bg-white p-2 rounded">
                        "{request.message}"
                      </p>
                    )}
                    
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleSendInvite(request, "contractor")}
                        disabled={processingId === request.email}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      >
                        {processingId === request.email ? (
                          <span className="animate-pulse">Sending...</span>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-1" />
                            Send Invite
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDecline(request, "contractor")}
                        disabled={processingId === request.email}
                        className="text-slate-600"
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === "supplier" && (
          <>
            {requests.supplier_requests.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Package className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                <p>No pending supplier requests</p>
              </div>
            ) : (
              <div className="space-y-3">
                {requests.supplier_requests.map((request, index) => (
                  <div 
                    key={request.email || index}
                    className="bg-slate-50 rounded-lg p-4 border border-slate-200 hover:border-violet-300 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-slate-900">{request.company_name}</h4>
                        <p className="text-sm text-slate-600">{request.contact_name || "—"}</p>
                      </div>
                      <span className="px-2 py-1 bg-violet-100 text-violet-700 rounded text-xs font-medium">
                        {request.business_type?.replace("_", " ").toUpperCase() || "Supplier"}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-sm text-slate-600 mb-3">
                      <div>📧 {request.email}</div>
                      <div>📞 {request.phone}</div>
                      <div>📍 {request.city}, {request.state}</div>
                      <div>🏭 FEI: {request.fei_number || "Not provided"}</div>
                    </div>
                    
                    {request.product_categories && (
                      <p className="text-sm text-slate-500 mb-3">
                        <strong>Products:</strong> {request.product_categories}
                      </p>
                    )}
                    
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleSendInvite(request, "supplier")}
                        disabled={processingId === request.email}
                        className="flex-1 bg-violet-600 hover:bg-violet-700"
                      >
                        {processingId === request.email ? (
                          <span className="animate-pulse">Sending...</span>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-1" />
                            Send Invite
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDecline(request, "supplier")}
                        disabled={processingId === request.email}
                        className="text-slate-600"
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer - Link to recruitment */}
      <div className="bg-slate-50 px-4 py-3 border-t border-slate-200">
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Need more leads? Use the Recruitment Dashboard
          </p>
          <div className="flex gap-2">
            <a 
              href="/contractor/recruitment"
              className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1"
            >
              Contractors <ChevronRight className="w-4 h-4" />
            </a>
            <a 
              href="/supplier/recruitment"
              className="text-sm text-violet-600 hover:text-violet-700 font-medium flex items-center gap-1"
            >
              Suppliers <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
